import type {CourseThemeDocument} from 'course/models/courseThemeDocument';
import {createContext, useContext, type ComponentType} from 'react';

/**
 * Optional component overrides an external deploy wrapper can provide to
 * customise CIP's application shell without patching its source files.
 *
 * Each key is optional. When a key is present its value replaces the
 * corresponding CIP default; when the key is absent the default is used.
 *
 * @example
 * ```tsx
 * // PAI wrapper — supply a custom landing page, keep everything else.
 * <AppOverridesContext.Provider value={{ SplashPage: PaiSplashPage }}>
 *   <Router />
 * </AppOverridesContext.Provider>
 * ```
 */
export interface AppOverrides {
  /**
   * Override for the landing/splash page rendered at the index route (`/`).
   * When omitted, the default CIP {@link Landing} component is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  SplashPage?: ComponentType<any>;
  /**
   * Override for the full-screen loading overlay shown while the app
   * initialises. When omitted, the default CIP {@link LoadingComponent} is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  LoadingPage?: ComponentType<any>;
  /**
   * Override for the error page shown when an unhandled error occurs. Receives
   * the same props as the default CIP {@link ErrorPage}. When omitted, the
   * default CIP {@link ErrorPage} is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  ErrorPage?: ComponentType<any>;
  /**
   * The application name used as the browser tab title at the root route (`/`).
   * When omitted, defaults to `"Code in Place"`.
   */
  appName?: string;
  /**
   * A second field on the `global/webapp` document, next to the shared
   * `version`, whose change prompts this app's open tabs to reload. Lets a
   * deploy wrapper nudge only its own users after a deploy. When omitted,
   * only `version` is watched.
   */
  versionField?: string;
  /**
   * Override providing this deploy wrapper's own nested routes, mounted at
   * `/:courseId/tools/*` (nested under the course route so switching between
   * a tool and the rest of the course doesn't unmount/remount CourseProvider
   * and friends). Must render its own `<Routes>`/`<Route>` tree (see
   * `react-router-dom`) — CIP mounts it as-is with no props. Intended for
   * deploy wrappers (e.g. PAI) that ship a growing collection of standalone
   * interactive tools without ever needing a CIP source change per tool. When
   * omitted, `/:courseId/tools/*` renders a 404 — CIP itself ships no tools.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  ToolRoutes?: ComponentType<any>;
  /**
   * Eager replacement for the Math IDE assignment route (`mide/a/:assignmentId`).
   * The Math IDE is a heavy subsystem, so CIP's default loads it with
   * `React.lazy` — the course chunk never downloads it for students who never
   * open it. A deploy wrapper MAY supply an eagerly-imported component here to
   * get it into its initial bundle, but weigh the cost: the eager import sits
   * in front of every page, including the landing page (this is why PAI
   * stopped using it — see
   * [#3616](https://github.com/CodeinPlace/cip/issues/3616)). When omitted,
   * the lazy default is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  MathIdeRoute?: ComponentType<any>;
  /**
   * Eager replacement for the Math assignment editor route
   * (`mathassignments/:assignmentId`). Lazy by default (see {@link MathIdeRoute}
   * for the rationale). When omitted, the lazy default is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  MathAssignmentRoute?: ComponentType<any>;
  /**
   * Eager replacement for the slide editor/presentation routes (`slides/*`).
   * The slide editor is a heavy subsystem, so CIP's default loads it with
   * `React.lazy`. See {@link MathIdeRoute} for the bundle-size trade-off of
   * supplying an eager component. When omitted, the lazy default is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  SlidesRoutes?: ComponentType<any>;
  /**
   * A course theme supplied by the deploy wrapper, applied to every course
   * rendered in this deploy. Provides the deploy's look and feel (colors, fonts,
   * brand) synchronously — no Firestore round-trip — while remaining fully
   * generic: it is a partial {@link CourseThemeDocument}, identical in shape to
   * the per-course Firestore theme doc. {@link CourseThemeProvider} folds this in
   * beneath any per-course Firestore theme, so a specific course can still
   * override individual fields. When omitted, only the Firestore theme (or the
   * hardcoded defaults) applies. See {@link AppOverrides} usage in the deploy's
   * entrypoint (e.g. PAI's `main.tsx`).
   */
  courseTheme?: Partial<CourseThemeDocument>;
  /**
   * Override for the course-home fallback used by the exit/back button
   * ({@link HomeExitButton}) when no flow-specific destination applies and
   * there's no navigation history to return to. When omitted, defaults to
   * `` `/${courseId}` ``.
   */
  defaultHomeUrl?: string;
  /**
   * Override for the course-home route (`/:courseId` and `/:courseId/home`).
   * Receives no props. When omitted, the default CIP {@link HomeRedirect} is
   * used, which redirects to a role-specific home (e.g. `studenthome`).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  CourseHomePage?: ComponentType<any>;
  /**
   * Override for the team page route (`/team`). Receives no props. When omitted,
   * the default CIP {@link Team} page is used. A deploy wrapper (e.g. PAI)
   * supplies its own team page here so the shared footer's "Team" link resolves
   * to a deploy-specific team rather than the CIP team.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  TeamPage?: ComponentType<any>;
  /**
   * Per-course overrides, keyed by courseId. When the active course matches a
   * key, the slots in {@link CourseAppOverrides} take precedence over the
   * deploy-wide slots above; any slot absent for that course falls back to the
   * deploy-wide value, then the CIP default. Only presentational slots are
   * course-scoped (see {@link CourseAppOverrides}); routing/eager slots stay
   * deploy-wide because they are bundle-structure decisions, not per-course UI.
   * How the active course is resolved from a URL is a consumer concern — the
   * loading overlay uses `resolveLoadingCourseId` (see
   * `components/loading/Loading.tsx`). When omitted, every course uses the
   * deploy-wide slots.
   */
  courseOverrides?: Record<string, CourseAppOverrides>;
}

/**
 * The subset of {@link AppOverrides} slots that can be overridden per-course
 * via {@link AppOverrides.courseOverrides}. Presentational only.
 */
export interface CourseAppOverrides {
  /**
   * Override for this course's standard (non-IDE) loading view. Unlike the
   * deploy-wide {@link AppOverrides.LoadingPage} — which replaces the entire
   * loading overlay including the IDE skeleton — this per-course override
   * replaces only the standard loading view (see `LoadingComponent` in
   * `components/loading/Loading.tsx`); IDE loads still show the IDE skeleton.
   * When omitted, the course uses the deploy-wide loader.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  LoadingPage?: ComponentType<any>;
}

/**
 * Context that holds the active {@link AppOverrides}. Provide a value at the
 * root of the tree (see {@link App}) to override specific CIP components.
 * Defaults to an empty object so every consumer is safe to call without a
 * provider.
 */
export const AppOverridesContext = createContext<AppOverrides>({});

/**
 * Returns the active {@link AppOverrides} from context.
 *
 * Deploy-wide slots are returned as-is. Per-course slots live under
 * {@link AppOverrides.courseOverrides}; consumers that vary by course resolve
 * the active course themselves (the loading overlay uses `resolveLoadingCourseId`
 * — see `components/loading/Loading.tsx`) and index into `courseOverrides`.
 *
 * @returns The nearest {@link AppOverridesContext} value. Defaults to `{}`
 *   (no overrides) when no provider is present.
 */
export function useAppOverrides(): AppOverrides {
  return useContext(AppOverridesContext);
}
